/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { oktaServerSuggestionsDismiss } from "../funcs/oktaServerSuggestionsDismiss.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaServerSuggestion } from "../models/components/oktaserversuggestion.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DismissOktaServerSuggestionRequest,
  DismissOktaServerSuggestionSecurity,
} from "../models/operations/dismissoktaserversuggestion.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DismissOktaServerSuggestionMutationVariables = {
  request: DismissOktaServerSuggestionRequest;
  security?: DismissOktaServerSuggestionSecurity | undefined;
  options?: RequestOptions;
};

export type DismissOktaServerSuggestionMutationData = OktaServerSuggestion;

export type DismissOktaServerSuggestionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * dismiss oktaServerSuggestions
 *
 * @remarks
 * Dismiss a suggestion currently made to the organization. Remembered until restored, even across Okta reconnects. Requires org:admin and the okta-connections rollout.
 */
export function useDismissOktaServerSuggestionMutation(
  options?: MutationHookOptions<
    DismissOktaServerSuggestionMutationData,
    DismissOktaServerSuggestionMutationError,
    DismissOktaServerSuggestionMutationVariables
  >,
): UseMutationResult<
  DismissOktaServerSuggestionMutationData,
  DismissOktaServerSuggestionMutationError,
  DismissOktaServerSuggestionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDismissOktaServerSuggestionMutation(client, options),
    ...options,
  });
}

export function mutationKeyDismissOktaServerSuggestion(): MutationKey {
  return ["@gram/client", "oktaServerSuggestions", "dismiss"];
}

export function buildDismissOktaServerSuggestionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DismissOktaServerSuggestionMutationVariables,
  ) => Promise<DismissOktaServerSuggestionMutationData>;
} {
  return {
    mutationKey: mutationKeyDismissOktaServerSuggestion(),
    mutationFn: function dismissOktaServerSuggestionMutationFn({
      request,
      security,
      options,
    }): Promise<DismissOktaServerSuggestionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(oktaServerSuggestionsDismiss(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
